<div cdkDrag>
    <div
        class="border rounded-xl"
        [ngClass]="{
            'p-4 flex flex-col justify-start items-stretch gap-4 bg-white': !isOverwritten,
            'bg-gray-100 bg-opacity-25 collapse collapse-arrow': isOverwritten
        }"
    >
        <input class="appearance-none opacity-0" type="checkbox" />
        <div class="w-full" [ngClass]="{ 'collapse-title': isOverwritten }">
            <div class="flex flex-row gap-2 items-center relative">
                <ng-container *ngIf="isOverwritten">
                    <span>{{ pricingOption?.optionKey }}</span>
                    <span *ngIf="pricingOption?.productOptionInternalNameKey"> / </span>
                    <span *ngIf="pricingOption?.productOptionInternalNameKey">{{ pricingOption?.productOptionInternalNameKey }}</span>
                </ng-container>

                <span *ngIf="pricingOption.companyId" class="badge badge-accent" i18n>Company specific</span>
                <span *ngIf="isOverride" class="badge badge-warning" i18n>Overwrites default option(s)</span>
                <span *ngIf="isOverwritten" class="badge badge-warning" i18n>Overridden by company-specific option</span>

                <div class="flex-1"></div>
                <button class="btn btn-sm btn-circle btn-ghost btn-accent" *ngIf="!editMode && editable" (click)="enableEditing()">
                    <iota-icon icon="pencil-square"></iota-icon>
                </button>
                <!-- z-10 lifts the button above the collapse toggle, which otherwise covers the whole title row. -->
                <button
                    class="btn btn-sm btn-ghost btn-accent gap-2 rounded-full relative z-10"
                    *ngIf="!editMode"
                    [disabled]="!duplicable"
                    (click)="duplicatePricingOption()"
                >
                    <iota-icon icon="document-duplicate"></iota-icon>
                    <span i18n>Duplicate</span>
                </button>
                <button class="btn btn-sm btn-circle btn-ghost btn-accent" *ngIf="!editMode && editable" (click)="deletePricingOption()">
                    <iota-icon icon="trash"></iota-icon>
                </button>
                <div
                    *ngIf="editMode"
                    [ngClass]="{ 'tooltip tooltip-bottom': !pricingOptionForm.valid }"
                    data-tip="Some fields are invalid or missing."
                >
                    <button
                        class="btn btn-sm btn-circle btn-ghost btn-accent"
                        [disabled]="!pricingOptionForm.valid"
                        (click)="savePricingOption()"
                        i18n-data-tip
                    >
                        <iota-icon flaticon="floppy-disk" size="1x"></iota-icon>
                    </button>
                </div>
                <button class="btn btn-sm btn-circle btn-ghost btn-accent" *ngIf="editMode" (click)="onCancel()">
                    <iota-icon icon="x-mark"></iota-icon>
                </button>
                <div
                    *ngIf="editable"
                    [ngClass]="{ 'tooltip tooltip-left': editMode }"
                    data-tip="Sorting will become available again when you save your data and you leave edit mode."
                    i18n-data-tip
                >
                    <button cdkDragHandle [ngClass]="{ 'btn-disabled': editMode }" class="btn btn-sm btn-circle btn-ghost btn-primary">
                        <iota-icon icon="chevron-up-down" [size]="1.5"></iota-icon>
                    </button>
                </div>
            </div>
        </div>

        <div class="grid grid-cols-4 items-center gap-2" [ngClass]="{ 'collapse-content': isOverwritten }" [formGroup]="pricingOptionForm">
            <span class="label-text" i18n>Name</span>
            <div class="col-span-3 flex flex-col items-stretch gap-1">
                <input
                    class="input input-sm input-bordered"
                    i18n-placeholder
                    placeholder="Premium"
                    formControlName="optionName"
                    [ngClass]="{ 'border-error': pricingOptionForm.get('optionName').invalid }"
                />
                <span
                    class="text-xs text-error"
                    *ngIf="pricingOptionForm.get('optionName').dirty && pricingOptionForm.get('optionName').invalid"
                    i18n
                    >The name is required.</span
                >
            </div>

            <span class="label-text" i18n>Solution partner product name</span>
            <input
                class="col-span-3 input input-sm input-bordered transition-colors"
                formControlName="reportingName"
                [ngClass]="{ 'border-error': pricingOptionForm.get('reportingName').invalid }"
            />

            <span class="label-text" i18n>Customer reporting name</span>
            <input
                class="col-span-3 input input-sm input-bordered transition-colors"
                formControlName="customerReportingName"
                [ngClass]="{ 'border-error': pricingOptionForm.get('customerReportingName').invalid }"
            />

            <span class="label-text" i18n>Key</span>
            <div class="col-span-3 flex flex-col items-stretch gap-1">
                <input
                    class="input input-sm input-bordered transition-colors"
                    placeholder="BUDGET"
                    formControlName="optionKey"
                    [ngClass]="{ 'border-error': pricingOptionForm.get('optionKey').invalid }"
                />
                <span
                    class="text-xs text-error"
                    *ngIf="pricingOptionForm.get('optionKey').dirty && pricingOptionForm.get('optionKey').invalid"
                    i18n
                    >The key is required.</span
                >
            </div>

            <span class="flex flex-row items-center gap-2"
                ><span class="label-text" i18n>Internal key</span>
                <div
                    class="tooltip"
                    data-tip="The internal key is used for code-switching to display the list of benefits for this option in the employee portal"
                    i18n-data-tip
                >
                    <iota-icon icon="question-mark-circle" class="text-white fill-white"></iota-icon></div
            ></span>
            <input
                class="col-span-3 input input-sm input-bordered"
                i18n-placeholder
                placeholder="LEVEL_1"
                formControlName="productOptionInternalNameKey"
            />

            <span class="label-text" i18n>Value</span>
            <select
                class="select select-sm select-bordered"
                [disabled]="!editMode"
                [(ngModel)]="valueType"
                (change)="resetOptionValue()"
                [ngModelOptions]="{ standalone: true }"
            >
                <option [ngValue]="valueTypes.NUMBER" i18n>Number</option>
                <option [ngValue]="valueTypes.DATE" i18n>Date</option>
                <option [ngValue]="valueTypes.TEXT" i18n>Text</option>
            </select>

            <div class="col-span-2 form-control" [ngSwitch]="valueType">
                <input
                    class="input input-sm input-bordered"
                    *ngSwitchCase="valueTypes.NUMBER"
                    type="number"
                    formControlName="optionNumberValue"
                />
                <input
                    class="input input-sm input-bordered"
                    *ngSwitchCase="valueTypes.DATE"
                    type="date"
                    formControlName="optionDateValue"
                />
                <input
                    class="input input-sm input-bordered"
                    *ngSwitchCase="valueTypes.TEXT"
                    type="text"
                    formControlName="optionTextValue"
                />
            </div>

            <div class="col-span-4"></div>

            <div></div>
            <div class="form-control">
                <label class="label justify-start cursor-pointer gap-2">
                    <input type="checkbox" class="checkbox" formControlName="recommended" />
                    <span class="label-text" i18n>Flag as recommended</span>
                    <div class="shrink-0">
                        <iota-icon
                            icon="question-mark-circle"
                            matTooltip="If selected this price option will be highlighted as recommended in Employee Portal. This requires several pricing options."
                            i18n-matTooltip
                            matTooltipClass="bg-iota-tooltip-bg whitespace-pre-line"
                        ></iota-icon>
                    </div>
                </label>
            </div>
            <div class="form-control">
                <label class="label justify-start cursor-pointer gap-2">
                    <input type="checkbox" class="checkbox" formControlName="useProductPriceScale" />
                    <span class="label-text" i18n>Use price tier from product master</span>
                    <div class="shrink-0">
                        <iota-icon
                            icon="question-mark-circle"
                            matTooltip="Uses the price tiers defined in the product master instead of the default price fields. The price tiers can be configured in the product master."
                            i18n-matTooltip
                            matTooltipClass="bg-iota-tooltip-bg whitespace-pre-line"
                        ></iota-icon>
                    </div>
                </label>
            </div>
            <div class="form-control">
                <label class="label justify-start cursor-pointer gap-2">
                    <input type="checkbox" class="checkbox" formControlName="externalPricingApproval" />
                    <span class="label-text" i18n>Price approval by solution partner</span>
                    <div class="shrink-0">
                        <iota-icon
                            icon="question-mark-circle"
                            matTooltip="Selected product subscriptions are initially created without pricing. Pricing is applied only after external approval by the solution partner, confirming the service was used."
                            i18n-matTooltip
                            matTooltipClass="bg-iota-tooltip-bg whitespace-pre-line"
                        ></iota-icon>
                    </div>
                </label>
            </div>

            <div class="col-span-4"></div>

            <ng-container *ngIf="!useProductPriceScale">
                <span class="label-text" i18n>Costs for employees</span>
                <label class="input-group input-group-sm">
                    <input class="flex-1 input input-sm input-bordered min-w-0" type="number" formControlName="optionPricing" />
                    <span>€</span>
                </label>

                <span class="justify-self-center label-text" i18n>per</span>
                <select class="select select-sm select-bordered" formControlName="optionPricingPeriod">
                    <option [ngValue]="undefined">-</option>
                    <option *ngFor="let periodOption of optionPricingPeriodOptions" [ngValue]="periodOption?.value">
                        {{ periodOption?.label }}
                    </option>
                </select>

                <span class="label-text" i18n>Price customer monthly</span>
                <label class="input-group input-group-sm">
                    <input class="flex-1 input input-sm input-bordered min-w-0" type="number" formControlName="productOptionCostsMonthly" />
                    <span>€</span>
                </label>

                <span class="label-text" i18n>Price customer annually</span>
                <label class="input-group input-group-sm">
                    <input class="flex-1 input input-sm input-bordered min-w-0" type="number" formControlName="productOptionCostsYearly" />
                    <span>€</span>
                </label>
            </ng-container>

            <ng-container *ngIf="useProductPriceScale">
                <div class="col-span-4" *ngIf="priceScaleLoading()" skeleton skeletonClasses="w-full h-40"></div>

                <app-feedback-banner
                    *ngIf="priceScaleError()"
                    class="col-span-4"
                    variant="warning"
                    i18n-header
                    header="Price tiers could not be loaded"
                    i18n-message
                    message="Please try again later."
                    [showCloseIcon]="false"
                ></app-feedback-banner>

                <app-feedback-banner
                    *ngIf="priceScaleMissing()"
                    class="col-span-4"
                    variant="warning"
                    i18n-message
                    message="No price tiers have been defined for this product yet."
                    [showCloseIcon]="false"
                ></app-feedback-banner>

                <fieldset *ngIf="priceScale()" class="col-span-4 border border-iota-outline rounded-lg">
                    <legend class="px-2 text-sm" i18n>Preview Price Tiers</legend>
                    <app-product-price-scale-form [priceScale]="priceScale()" [editMode]="false"></app-product-price-scale-form>
                </fieldset>
            </ng-container>

            <span class="label-text" i18n>Tax category</span>
            <select class="select select-sm select-bordered" formControlName="taxCategory">
                <option *ngFor="let option of taxCategoryOptions" [ngValue]="option.value">
                    {{ option.label }}
                </option>
            </select>

            <span class="label-text" i18n>Tax rate</span>
            <div class="flex flex-col items-stretch gap-1">
                <label class="w-full input-group input-group-sm">
                    <input
                        class="flex-1 input input-sm input-bordered min-w-0"
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        placeholder="0.19"
                        formControlName="taxRate"
                        [ngClass]="{ 'border-error': pricingOptionForm.get('taxRate').invalid }"
                    />
                    <span>%</span>
                </label>
                <span
                    class="text-xs text-error"
                    *ngIf="pricingOptionForm.get('taxRate').dirty && pricingOptionForm.get('taxRate').invalid"
                    i18n
                    >The value must be between 0 and 1</span
                >
            </div>
        </div>
    </div>
</div>
